import { useState, useRef, useEffect } from 'react';
import styles from './RHTV-Logo.module.scss';
import className from 'classnames/bind';
import Image from 'next/image';
const cx = className.bind(styles);

// Assets
import LogoVideo from '@/assets/videos/rhtv/logo-spin.mp4';
import FallbackImage from '@/assets/images/rhtv/logo-spin-fallback.png';

// ----------------------------------------

interface RHTV_LogoProps {
  className?: string;
}

export default function RHTV_Logo({ className: parentClassName }: RHTV_LogoProps) {

  const [isVideoReady, setIsVideoReady] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const DEV_SIMULATE_LOW_POWER_MODE = false;

  // Force fallback mode in development if simulating low power mode
  useEffect(() => {
    if (DEV_SIMULATE_LOW_POWER_MODE) {
      setUseFallback(true);
      setIsVideoReady(true); // Treat fallback as "ready" for styling
    }
  }, []);

  // -------------------- Handle low power mode
  // If video is unable to autoplay (low power mode, etc), replace with fallback image

  useEffect(() => {
    const video = videoRef.current;
    if (!video || DEV_SIMULATE_LOW_POWER_MODE) return;

    // Check if video is already loaded (race condition - loaded before event handler attached)
    if (video.readyState >= 2) { // HAVE_CURRENT_DATA or better
      setIsVideoReady(true);
    }

    // Try to play the video, if it fails (low power mode, autoplay blocked), use fallback
    const playPromise = video.play();

    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Autoplay blocked - use fallback image
        setUseFallback(true);
        setIsVideoReady(true); // Treat fallback as "ready" for styling
      });
    }
  }, []);

  // -------------------- Render

  return (
    <div className={cx("rhtv-logo", { "video-ready": isVideoReady }, parentClassName)}
      role="img"
      aria-label="RRTV logo"
    >
      {useFallback ? (
        <Image
          src={FallbackImage}
          alt=""
          className={cx("logo-spin")}
          priority
        />
      ) : (
        <video
          ref={videoRef}
          className={cx("logo-spin")}
          src={LogoVideo}
          loop
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
          onLoadedData={() => setIsVideoReady(true)}
        />
      )}
    </div>
  );
}
